// ARQUIVO ATUALIZADO
import React, { useMemo, useState, useRef, useEffect } from 'react';
import useData from '../../hooks/useData';
import Modal from '../ui/Modal';
import GlassButton from '../ui/Button';
import { getPriorityColor, getStatusPillClass, getPriorityPillClass } from '../../utils/helpers';
import { ChevronLeftIcon, ChevronRightIcon, RefreshIcon } from '../ui/Icons';

const TaskModal = ({ day, tasks, onClose, onTaskClick }) => {
    if (!day) return null;

    const formattedDate = day.dateObj.toLocaleDateString('pt-BR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
    });
    
    const modalTitle = `Tarefas de ${formattedDate}`;
    const footer = (<GlassButton onClick={onClose}>Fechar</GlassButton>);

    return (
        <Modal isOpen={true} onClose={onClose} title={modalTitle} footer={footer} maxWidth="max-w-lg">
            <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                {tasks.length > 0 ? tasks.map(task => (
                    <button 
                        key={task.id}
                        onClick={() => { onTaskClick(task); onClose(); }}
                        className="w-full text-left p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors flex items-center gap-3"
                    >
                        <div className="w-2 h-6 rounded-md" style={{ backgroundColor: getPriorityColor(task.priority) }}></div>
                        <span className="text-white uppercase">{task.title}</span>
                    </button>
                )) : ( <p className="text-gray-400 text-center py-4">Nenhuma tarefa para este dia.</p> )}
            </div>
        </Modal>
    );
};

const SummaryItem = ({ label, value, colorClass, onClick, isActive }) => (
    <button 
        onClick={onClick} 
        className={`text-center p-2 rounded-lg transition-all duration-200 w-full ${isActive ? 'bg-indigo-500 ring-2 ring-indigo-300' : 'bg-black/20 hover:bg-black/40'}`}
    >
        <p className={`text-2xl font-bold ${colorClass}`}>{value}</p>
        <p className="text-[10px] text-gray-400 uppercase tracking-wider">{label}</p>
    </button>
);

const TaskListRow = ({ task, onClick }) => {
    const { tags: allTags } = useData();

    const getDayOfWeek = (deadline) => {
        if (!deadline) return '';
        const date = new Date(deadline);
        return date.toLocaleDateString('pt-BR', { weekday: 'short', timeZone: 'UTC' }).replace('.', '').toUpperCase();
    };
    
    return (
        <button onClick={() => onClick(task)} className="w-full flex justify-between items-center p-2 rounded-lg hover:bg-white/10 transition-colors text-left">
            <div>
                <p className="text-sm text-white truncate uppercase">{task.title}</p>
                <p className="text-xs text-gray-400">{task.client || 'Sem cliente'}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
                {task.isAlteration && (
                    <div className="text-orange-400" title="Tarefa marcada para alteração">
                        <RefreshIcon size={16} />
                    </div>
                )}
                <span className="text-xs font-semibold text-gray-300 w-10 text-center">{getDayOfWeek(task.deadline)}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] ${getPriorityPillClass(task.priority)}`}>{task.priority}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] ${getStatusPillClass(task.status)}`}>{task.status}</span>
            </div>
        </button>
    );
};

const WeekViewWidget = ({ onTaskClick }) => {
    const { tasks } = useData();
    const [selectedDay, setSelectedDay] = useState(null);
    const widgetRef = useRef(null);
    const [isCompact, setIsCompact] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [activeSummaryFilter, setActiveSummaryFilter] = useState(null);

    const handleSummaryClick = (filter) => {
        setActiveSummaryFilter(prev => (prev === filter ? null : filter));
    };

    const handlePrevWeek = () => { setCurrentDate(prev => { const d = new Date(prev); d.setDate(d.getDate() - 7); return d; }); };
    const handleNextWeek = () => { setCurrentDate(prev => { const d = new Date(prev); d.setDate(d.getDate() + 7); return d; }); };
    const handleGoToToday = () => { setCurrentDate(new Date()); };

    useEffect(() => {
        const observer = new ResizeObserver(entries => {
            if (entries && entries.length > 0) {
                const { width } = entries[0].contentRect;
                setIsCompact(width < 350);
            }
        });
        if (widgetRef.current) observer.observe(widgetRef.current);
        return () => observer.disconnect();
    }, []);

    const { weekDays, weeklySummary } = useMemo(() => {
        const weekDaysData = [];
        const todayForCalc = new Date(currentDate);
        const realToday = new Date();
        realToday.setHours(0, 0, 0, 0);

        for (let i = 0; i < 7; i++) {
            const date = new Date(todayForCalc);
            date.setDate(todayForCalc.getDate() + i);
            const dateString = date.toISOString().split('T')[0];
            const tasksOnDay = tasks.filter(t => t.deadline === dateString && !t.isArchived)
                                     .sort((a, b) => { // Ordena por prioridade
                                         const priorityOrder = { 'Alta': 1, 'Média': 2, 'Baixa': 3 };
                                         return (priorityOrder[a.priority] || 4) - (priorityOrder[b.priority] || 4);
                                     });

            weekDaysData.push({ 
                day: date.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '').toUpperCase(), 
                date: date.getDate(), 
                taskCount: tasksOnDay.length, 
                isToday: date.toDateString() === realToday.toDateString(), 
                dateObj: date, 
                tasks: tasksOnDay 
            });
        }
        
        const allTasksInWeek = weekDaysData.flatMap(day => day.tasks);
        const summary = {
            total: allTasksInWeek.length,
            priorities: { Alta: allTasksInWeek.filter(t => t.priority === 'Alta').length, Média: allTasksInWeek.filter(t => t.priority === 'Média').length },
            overdue: allTasksInWeek.filter(t => { const taskDate = new Date(t.deadline); const tzOffset = taskDate.getTimezoneOffset() * 60000; const utcTaskDate = new Date(taskDate.getTime() + tzOffset); return utcTaskDate < realToday && t.status !== 'Concluído'; }).length,
        };
        return { weekDays: weekDaysData, weeklySummary: summary };
    }, [tasks, currentDate]);

    const { filteredTasks, filterTitle } = useMemo(() => {
        if (!activeSummaryFilter) return { filteredTasks: [], filterTitle: '' };

        const allTasksInWeek = weekDays.flatMap(day => day.tasks);
        const realToday = new Date();
        realToday.setHours(0, 0, 0, 0);

        switch (activeSummaryFilter) {
            case 'total':
                return { filteredTasks: allTasksInWeek, filterTitle: 'Todas as Tarefas da Semana' };
            case 'overdue':
                const overdueTasks = allTasksInWeek.filter(t => {
                    const taskDate = new Date(t.deadline);
                    const tzOffset = taskDate.getTimezoneOffset() * 60000;
                    const utcTaskDate = new Date(taskDate.getTime() + tzOffset);
                    return utcTaskDate < realToday && t.status !== 'Concluído';
                });
                return { filteredTasks: overdueTasks, filterTitle: 'Tarefas Atrasadas' };
            case 'alta':
                return { filteredTasks: allTasksInWeek.filter(t => t.priority === 'Alta'), filterTitle: 'Prioridade Alta' };
            case 'media':
                 return { filteredTasks: allTasksInWeek.filter(t => t.priority === 'Média'), filterTitle: 'Prioridade Média' };
            default:
                return { filteredTasks: [], filterTitle: '' };
        }
    }, [activeSummaryFilter, weekDays]);

    const headerDateText = useMemo(() => {
        const firstDayOfWeek = weekDays[0]?.dateObj;
        const lastDayOfWeek = weekDays[6]?.dateObj;
        if (!firstDayOfWeek || !lastDayOfWeek) return '';
        const startMonth = firstDayOfWeek.toLocaleString('pt-BR', { month: 'long' });
        const endMonth = lastDayOfWeek.toLocaleString('pt-BR', { month: 'long' });
        const year = firstDayOfWeek.getFullYear();
        return startMonth === endMonth ? `${startMonth} de ${year}` : `${startMonth} / ${endMonth} de ${year}`;
    }, [weekDays]);

    return (
        <>
            <div ref={widgetRef} className="p-4 h-full flex flex-col animate-fade-in overflow-y-auto">
                <div className="flex-shrink-0">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className={`font-bold text-white transition-all ${isCompact ? 'text-base' : 'text-lg'}`}>{headerDateText}</h3>
                        <div className="flex items-center gap-1"><button onClick={handlePrevWeek} className="p-2 rounded-full hover:bg-white/10 transition-colors"><ChevronLeftIcon size={18} /></button><GlassButton onClick={handleGoToToday} variant="minimal" className="!py-1 !px-3 !text-xs">Hoje</GlassButton><button onClick={handleNextWeek} className="p-2 rounded-full hover:bg-white/10 transition-colors"><ChevronRightIcon size={18} /></button></div>
                    </div>
                    {/* --- INÍCIO DA ALTERAÇÃO: Novo layout para os dias da semana --- */}
                    <div className="grid grid-cols-7 text-center gap-2">
                        {weekDays.map((day, index) => (
                            <button 
                                key={index} 
                                onClick={() => day.taskCount > 0 && setSelectedDay(day)} 
                                className={`relative p-2 rounded-lg transition-all duration-200 flex flex-col justify-between h-24 ${day.isToday ? 'bg-white/10 ring-2 ring-indigo-400' : 'bg-white/5'} ${day.taskCount > 0 ? 'hover:bg-white/15 cursor-pointer' : 'cursor-default'}`}
                            >
                                <div className="text-center w-full">
                                    <p className="font-bold text-xs text-gray-300">{day.day}</p>
                                    <p className={`font-bold text-2xl mt-1 ${day.isToday ? 'text-white' : 'text-gray-400'}`}>{day.date}</p>
                                </div>
                                
                                {day.taskCount > 0 && (
                                    <div className="flex items-center justify-center gap-1 h-2 w-full mt-2">
                                        {day.tasks.slice(0, 4).map(task => (
                                            <div
                                                key={task.id}
                                                className="w-2 h-2 rounded-full"
                                                style={{ backgroundColor: getPriorityColor(task.priority) }}
                                                title={`${task.title} - Prioridade: ${task.priority}`}
                                            />
                                        ))}
                                        {day.taskCount > 4 && (
                                            <span className="text-[9px] text-gray-400 font-bold -ml-0.5">+{day.taskCount - 4}</span>
                                        )}
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>
                    {/* --- FIM DA ALTERAÇÃO --- */}
                </div>
                
                <div className="pt-4 border-t border-white/10 mt-4 flex-shrink-0">
                    <div className="grid grid-cols-4 gap-2">
                        <SummaryItem label="Total" value={weeklySummary.total} colorClass="text-white" onClick={() => handleSummaryClick('total')} isActive={activeSummaryFilter === 'total'} />
                        <SummaryItem label="Atrasadas" value={weeklySummary.overdue} colorClass="text-red-400" onClick={() => handleSummaryClick('overdue')} isActive={activeSummaryFilter === 'overdue'} />
                        <SummaryItem label="Prior. Alta" value={weeklySummary.priorities.Alta} colorClass="text-red-400" onClick={() => handleSummaryClick('alta')} isActive={activeSummaryFilter === 'alta'} />
                        <SummaryItem label="Prior. Média" value={weeklySummary.priorities.Média} colorClass="text-yellow-400" onClick={() => handleSummaryClick('media')} isActive={activeSummaryFilter === 'media'} />
                    </div>
                </div>

                {activeSummaryFilter && (
                    <div className="mt-4 animate-fade-in flex-grow min-h-0 flex flex-col">
                        <h4 className="text-sm font-semibold text-gray-300 mb-2 px-1 flex-shrink-0">{filterTitle} ({filteredTasks.length})</h4>
                        <div className="overflow-y-auto space-y-1 pr-1 flex-grow">
                            {filteredTasks.length > 0 ? (
                                filteredTasks.map(task => <TaskListRow key={task.id} task={task} onClick={onTaskClick} />)
                            ) : (
                                <div className="flex items-center justify-center h-full">
                                    <p className="text-center text-sm text-gray-400 py-4">Nenhuma tarefa encontrada.</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            {selectedDay && <TaskModal day={selectedDay} tasks={selectedDay.tasks} onClose={() => setSelectedDay(null)} onTaskClick={onTaskClick}/>}
        </>
    );
};

export default WeekViewWidget;